// Russian for the quality namespace (Module 07 §4, slice 07-A) — the design
// partner's working language, and the language a KazKabel qc lead configures
// control points and runs inspections in.
//
// INDUSTRIAL REGISTER, not generic UI Russian: контроль/замер/допуск at the
// instrument, партия/бобина/барабан for the subject, брак/причина брака for the
// failure, годен/брак for the verdict — the words a Kazakh cable plant's OTK
// already uses on paper.
//
// THE THREE WORDS THAT MUST STAY THREE WORDS (D-123 ruling 9), settled here in
// Russian at the same time as in English rather than left to a later
// translator, because a term settled in one language is not settled:
//
//   module 07's quarantine record   → КАРАНТИН
//   module 04's operation flag      → блокировка операции по качеству
//   module 06's reservation         → резерв
//
// Since 07-B-3 the word sits on CONTROLS: the quarantine board (§4.6), the
// §4.4 failure-action sheet and the §4.3 money chips all say «карантин», the
// term this file fixed in 07-A. Module 04's flag stays «блокировка операции
// по качеству» (queue.blocks.in_process, editor.blockingHint), module 06's
// reservations stay «резерв» — three things, three words, in both locales.
//
// The type is enforced by `QualityDict` in quality.i18n.ts: a missing or extra
// key fails `tsc`, and tools/ci/i18n-parity.ts fails if any value here ever
// regresses to its English text.

import type { QualityDict } from "./quality.i18n.js";

export const qualityRuDict: QualityDict = {
  nav: {
    aria: "Разделы качества",
    inspections: "Проверки",
    quarantine: "Карантин",
    controlPoints: "Точки контроля",
    failureCauses: "Причины брака",
    coa: "Сертификаты",
    analytics: "Аналитика",
  },

  // §4.8 — аналитика качества (07-E).
  analytics: {
    title: "Аналитика качества",
    refresh: "Обновить проекцию",
    asOf: "По состоянию на {{at}} (обновление проекции)",
    filterFrom: "С",
    filterTo: "По",
    filterFacility: "Площадка",
    allFacilities: "Все площадки",
    filterDimension: "Группировка",
    filterBy: "Ранжировать",
    filterDate: "Дата",
    fpyLabel: "Выход с первого предъявления (по проверкам)",
    trendTitle: "Выход с первого предъявления (по проверкам) — тренд",
    trendLatest: "Последнее значение: {{value}} за {{day}}",
    leagueTitle: "Выход с первого предъявления (по проверкам) по измерению",
    filterGrain: "Шаг",
    grainDaily: "По дням",
    grainWeekly: "По неделям",
    unstampedKey: "(измерение не проставлено)",
    unattributed: "(смена не проставлена)",
    paretoTitle: "Парето причин брака",
    scoreboardTitle: "Сводка по сменам",
    supplierTitle: "Брак при приёмке по поставщикам",
    dimProduct: "Продукция",
    dimWorkCenter: "Рабочий центр",
    dimShift: "Смена выпуска",
    byCount: "По количеству",
    byValue: "По стоимости риска",
    colKey: "Измерение",
    colTotal: "Проверки",
    colPassedFirstTime: "С первого раза",
    colCause: "Причина",
    colCount: "Количество",
    colValue: "Стоимость риска",
    colShift: "Смена",
    colInspections: "Проверки",
    colDefectsProduced: "Брак выпущен",
    colDefectsCaught: "Брак выявлен",
    colHandoffs: "Передачи брака",
    colSupplier: "Поставщик",
    colReceipts: "Поставки",
    colFailures: "Отказы",
    colFailureRate: "Доля отказов",
    colTopCause: "Основная причина",
    error: "Не удалось загрузить аналитику.",
    emptyTitle: "В этом периоде проверок нет",
    emptyBody: "Расширьте период или убедитесь, что проверки в нём завершались.",
    scoreEmptyTitle: "На эту дату смен нет",
    scoreEmptyBody: "Выберите дату, когда площадка работала по сменам.",
    supplierEmptyTitle: "В этом периоде нет приёмочных проверок",
    supplierEmptyBody: "Приёмочные выборки появятся после поставок контролируемых материалов.",
  },

  // §4.7 — сертификаты (07-E). Промышленный регистр: сертификат/партия/
  // бобина — слова, которыми ОТК уже говорит на бумаге.
  coa: {
    title: "Сертификаты (CoA)",
    tabDocuments: "Документы",
    tabTemplates: "Шаблоны",
    filterStatus: "Статус",
    allStatuses: "Все статусы",
    colNo: "Сертификат",
    colRevision: "Ред.",
    colKind: "Тип",
    colCustomer: "Заказчик",
    colLanguage: "Язык",
    colStatus: "Статус",
    colIssuedAt: "Выдан",
    colActions: "Действия",
    kind: { analysis: "Анализ", conformance: "Соответствие" },
    status: { draft: "Черновик", issued: "Выдан", revoked: "Отозван" },
    preview: "Предпросмотр",
    pdf: "PDF",
    copyLink: "Скопировать публичную ссылку",
    linkCopied: "Публичная ссылка скопирована.",
    issue: "Выдать",
    issued: "Сертификат {{coaNo}} выдан — публичная ссылка активна.",
    issueError: "Не удалось выдать сертификат.",
    revoke: "Отозвать",
    revokeTitle: "Отозвать {{coaNo}}",
    revokeWarning:
      "Отзыв немедленно закрывает ссылку заказчика — с этого момента по ней будет 404. Замороженный PDF остаётся в ваших записях.",
    reissueNext: "Создать следующую редакцию под тем же номером сертификата",
    revoked: "Сертификат {{coaNo}} отозван.",
    revokeError: "Не удалось отозвать сертификат.",
    fieldReason: "Причина",
    draftNew: "Создать сертификат",
    draftSubmit: "Создать черновик",
    drafted: "Черновик {{coaNo}} создан.",
    draftError: "Не удалось создать черновик сертификата.",
    fieldTemplate: "Шаблон",
    fieldLot: "Партия",
    fieldLotHint: "Идентификатор партии — каждый текущий сертификат создаётся от партии.",
    fieldCustomer: "Наименование заказчика",
    fieldCustomerHint: "Печатается на сертификате ровно так, как введено.",
    fieldLanguage: "Язык",
    fieldKind: "Тип",
    gapsTitle: "{{coaNo}} пока нельзя выдать — не хватает замеров или есть брак:",
    gapMissing: "Провести {{point}} на {{unit}}",
    gapNotPassed: "{{point}} не пройден на {{unit}} — сначала закройте несоответствие",
    error: "Не удалось загрузить сертификаты.",
    emptyTitle: "Сертификатов пока нет",
    emptyBody: "Создайте сертификат от партии, у которой пройден финальный контроль.",
    tplColName: "Шаблон",
    tplColBinding: "Привязка к продукции",
    tplColActive: "Состояние",
    tplActive: "Активен",
    tplInactive: "Неактивен",
    tplAllProducts: "Вся продукция",
    tplNew: "Новый шаблон",
    tplEdit: "Изменить",
    tplSave: "Сохранить шаблон",
    tplSaved: "Шаблон сохранён.",
    tplError: "Не удалось сохранить шаблон.",
    tplEmptyTitle: "Шаблонов пока нет",
    tplEmptyBody: "Создайте шаблон — без него сертификат не создать.",
    tplFieldName: "Наименование",
    tplFieldProduct: "Привязка к продукции",
    tplFieldProductHint: "Пусто — шаблон по умолчанию для всей продукции.",
    tplFieldHeaderEn: "Шапка (английский)",
    tplFieldHeaderRu: "Шапка (русский)",
    tplFieldSignatory: "Подписант",
  },

  common: {
    retry: "Повторить",
    requestId: "ID запроса",
    saving: "Сохранение…",
    cancel: "Отмена",
    loading: "Загрузка…",
    range: "{{from}}–{{to}} из {{total}}",
    prevPage: "Предыдущая страница",
    nextPage: "Следующая страница",
  },

  access: {
    title: "Не ваш экран",
    area: "Вашей роли недоступны экраны качества. Обратитесь к администратору группы.",
    queue:
      "Ваша роль не читает очередь проверок. Администратор группы может выдать доступ, если это часть вашей работы.",
    coa: "Ваша роль не видит сертификаты. Администратор группы может выдать доступ, если это часть вашей работы.",
    analytics:
      "Ваша роль не видит аналитику качества. Администратор группы может выдать доступ, если это часть вашей работы.",
    board:
      "Ваша роль не читает доску карантина. Администратор группы может выдать доступ, если это часть вашей работы.",
    library:
      "Ваша роль не читает справочник точек контроля. Администратор группы может выдать доступ, если это часть вашей работы.",
    causes:
      "Ваша роль не читает справочники качества. Администратор группы может выдать доступ, если это часть вашей работы.",
    editor: "Точки контроля настраивают ОТК и администраторы. Читать справочник вы можете.",
  },

  offline: {
    title: "Нет сети",
    body: "Устройство offline. Экран заполнится сам, как только связь вернётся — ничего не потеряно.",
  },

  stage: {
    receiving: "Входной контроль",
    in_process: "Операционный контроль",
    final: "Приёмочный контроль",
  },

  status: {
    open: "Не начата",
    in_progress: "В работе",
    passed: "Годен",
    failed: "Брак",
    cancelled: "Отменена",
  },

  verdict: {
    pass: "Годен",
    fail: "Брак",
    na: "Не применимо",
  },

  checkType: {
    pass_fail: "Годен / брак",
    measurement: "Замер",
    photo: "Фото",
    checklist: "Перечень",
  },

  sampleMode: {
    every_unit: "Каждая единица",
    fixed_count: "Фиксированное число",
    percentage: "Доля в процентах",
    first_n: "Первые N",
    aql: "План выборки AQL",
  },

  // 07-D — эхо плана выборки (§2.5, D-192): отображается, не пересчитывается.
  samplingEcho: {
    aql: "выборка {{n}} из {{lotSize}}, приёмочное число {{accept}}",
    lotWhole: "Вся партия (насыпная приёмка)",
  },

  sample: {
    every_unit: "Каждая единица",
    fixed_count: "{{n}} шт. с партии",
    first_n: "Первые {{n}}",
    percentage: "Выборка {{pct}}%",
    aql: "AQL {{value}}, уровень {{level}}",
  },

  causeCategory: {
    material: "Материал",
    machine: "Оборудование",
    method: "Технология",
    operator: "Оператор",
    environment: "Условия",
    unknown: "Не установлена",
  },

  locale: {
    en: "Английский",
    ru: "Русский",
    fr: "Французский",
    zh: "Китайский (упрощённый)",
    "zh-Hant": "Китайский (традиционный)",
    hi: "Хинди",
  },

  disposition: {
    release: "Снятие карантина",
    rework: "Доработка",
    scrap: "Списание",
    downgrade: "Уценка",
    return_to_supplier: "Возврат поставщику",
    guided_cut: "Разрез по дефекту",
  },

  board: {
    title: "Карантин",
    tabsAria: "Доска карантина",
    tab: {
      active: "Действующие",
      released: "Снятые",
      disposed: "Решённые",
    },
    tableCaption: "Продукция в карантине, новые сверху",
    colSelect: "Выбор",
    selectAll: "Отметить все карантины на странице",
    selectOne: "Отметить карантин {{holdNo}}",
    colNo: "№",
    colSubject: "Объект",
    colReason: "Причина",
    colInspection: "Проверка",
    inspectionLink: "Открыть проверку",
    manualHold: "Поставлен вручную",
    colAge: "В карантине",
    ageDays: "{{days}} дн",
    noAge: "—",
    colAtRisk: "Стоимость под риском",
    colPlacedBy: "Кто поставил",
    colProposal: "Предложение агента",
    proposalChip: "Предложено: {{kind}}",
    noProposal: "—",
    colDecision: "Решение",
    noSubject: "—",
    filterProduct: "Изделие",
    anyProduct: "Любое изделие",
    productFilterUnavailable: "Список изделий не загрузился — фильтр временно недоступен.",
    filterAge: "В карантине не меньше (дней)",
    loadMore: "Показать ещё",
    error: "Не удалось загрузить доску карантина",
    empty: {
      activeTitle: "В карантине ничего нет",
      activeBody:
        "Продукция попадает сюда автоматически при браке или когда карантин ставят вручную.",
      releasedTitle: "Снятых карантинов пока нет",
      releasedBody: "Карантин, снятый с обоснованием, собирается здесь.",
      disposedTitle: "Решённых карантинов пока нет",
      disposedBody:
        "Карантины с принятым решением — доработка, списание, уценка или возврат поставщику — собираются здесь.",
    },
    dispositionOpen: "Принять решение по выбранным: {{count}}…",
    dialogTitle: "Решение по карантину — позиций: {{count}}",
    dialogKind: "Решение",
    dialogKindPick: "Выберите, что делать с продукцией…",
    dialogKindRequired: "Сначала выберите решение.",
    dialogNotes: "Примечание",
    dialogJustificationRequired: "Для снятия карантина обязательно обоснование.",
    dialogNoteRequired: "Для уценки обязательно примечание.",
    dialogProposed: "Агент предложил: {{kind}}. Решение за вами.",
    // 07-D — §3.5c: решение по партии на карантине выборки приёмки. D-193:
    // формулировка whole_lot называет ПАРТИЮ — партия, принятая несколькими
    // поставками, бракуется целиком, и оператор читает это до нажатия.
    scopeLabel: "Решение по партии",
    scopePick: "Выберите, что будет с остальной поставкой…",
    scopeRequired: "Для карантина по выборке приёмки нужно ещё решение по партии.",
    scope: {
      sampled_only: "Только этот образец — освободить партию",
      whole_lot: "Забраковать всю партию",
      inspect_all: "Проверить каждую оставшуюся единицу",
    },
    scopeLotNote:
      "Решение применяется ко ВСЕЙ ПАРТИИ — партия, принятая несколькими поставками, бракуется целиком. Партия:",
    targetLabel: "Операция для повторного открытия — не обязательно",
    targetHint: "Вставьте ID операции заказа, которую откроет доработка",
    targetInvalid: "Вставьте полный ID операции или оставьте поле пустым.",
    // D-299 G5: карантин не принимает «принять отклонение» — только снятие с
    // обоснованием, списание и уценку.
    reworkOrderNotLive:
      "Доработка недоступна: заказ в статусе «{{status}}». Снимите карантин с обоснованием, спишите материал или уцените его.",
    reworkTargetNotLive:
      "Заказ в статусе «{{status}}». Укажите операцию действующего заказа или снимите карантин с обоснованием, спишите либо уцените материал.",
    reworkStepNotCompleted:
      "Доработка недоступна: операция {{seq}} в статусе «{{status}}», открывать заново нечего. Снимите карантин с обоснованием, спишите или уцените материал.",
    dialogConfirm: "Подтвердить для позиций: {{count}}",
    outcomeDone: {
      active: "Ещё действует",
      released: "Карантин снят",
      disposed: "Решение исполнено",
    },
    concurrent: "Уже решено — {{kind}}: {{user}}, {{hours}} ч назад.",
    concurrentMinutes: "Уже решено — {{kind}}: {{user}}, {{minutes}} мин назад.",
    concurrentJustNow: "Уже решено — {{kind}}: {{user}}, только что.",
    transportFailed:
      "Ответ не пришёл — неизвестно, записано ли решение. Обновите доску и проверьте.",
    refreshPrompt: "Кто-то принял решение раньше. Обновите доску, чтобы увидеть его.",
    refreshBoard: "Обновить доску",
  },

  library: {
    title: "Точки контроля",
    newPoint: "Новая точка контроля",
    tableCaption: "Точки контроля",
    colCode: "Код",
    colName: "Наименование",
    colStage: "Этап",
    colBindings: "Применяется к",
    colSample: "Выборка",
    colFlags: "Признаки",
    colVersion: "Версия",
    colActions: "Действия",
    versionCell: "в{{version}}",
    superseded: "заменена",
    blocking: "Блокирующая",
    fromPack: "Отраслевой пакет",
    custom: "Ваша",
    inactive: "Отключена",
    edit: "Изменить",
    duplicate: "Копировать",
    deactivate: "Отключить",
    deactivateFailed: "Не удалось отключить точку контроля.",
    filterStage: "Этап",
    allStages: "Все этапы",
    filterSource: "Источник",
    allSources: "Любой источник",
    filterActive: "Состояние",
    anyActive: "Включена или нет",
    onlyActive: "Включена",
    onlyInactive: "Отключена",
    filterVersions: "Версии",
    onlyCurrent: "Только текущие",
    allVersions: "Все версии",
    filterSearch: "Поиск",
    searchHint: "Код или наименование…",
    boundOnRouting: "Операция маршрута (задаётся в справочнике)",
    boundNothing: "Пока ни к чему",
    boundMaterials: "Материалов: {{count}}",
    boundProducts: "Изделий: {{count}}",
    boundSuppliers: "Поставщиков: {{count}}",
    error: "Не удалось загрузить точки контроля",
    emptyTitle: "Точек контроля пока нет",
    emptyBody:
      "Точка контроля задаёт, где проверяют качество и что замеряют. Установите шесть проверок кабельного пакета или создайте свои.",
    seedPack: "Установить проверки кабельного пакета",
    seedReport:
      "Установлено точек контроля: {{created}}, ваших не тронуто: {{skipped}}, добавлено причин брака: {{causes}}.",
    seedFailed: "Не удалось установить проверки пакета.",
  },

  // S4.1b — справочник причин брака (fix #117). «Причина брака» — слово ОТК,
  // «причина отходов» — слово модуля 05 (журнал отходов), и они намеренно
  // остаются двумя разными словами: сопоставление одного с другим и есть то,
  // что этот экран настраивает.
  causes: {
    title: "Причины брака",
    tableCaption: "Причины брака",
    explainer:
      "Причина брака объясняет, почему проверка не пройдена. Свяжите её с причиной отходов, чтобы по ней можно было списывать.",
    unmappedCount:
      "Причин без причины отходов: {{count}}. Списание по ним отклоняется, пока сопоставление не задано.",
    colCode: "Код",
    colName: "Название",
    colCategory: "Вид",
    colMapping: "Причина отходов",
    colActions: "Действия",
    unmapped: "Не сопоставлена",
    map: "Сопоставить",
    remap: "Изменить",
    filterCategory: "Вид",
    allCategories: "Любой вид",
    filterMapping: "Причина отходов",
    allMappings: "С сопоставлением и без",
    onlyUnmapped: "Только без сопоставления",
    error: "Не удалось загрузить причины брака",
    emptyTitle: "Причин брака пока нет",
    emptyBody: "Причины брака приходят с отраслевым пакетом, и ОТК может добавить свои.",
    emptyFiltered: "По этим фильтрам ничего нет.",
    dialogTitle: "Сопоставить {{code}} с причиной отходов",
    dialogBody:
      "Выберите причину отходов, под которой будет учитываться этот брак. Действует на списания с этого момента — уже принятые решения сохраняют то, что записали.",
    dialogField: "Причина отходов",
    dialogPickPlaceholder: "Выберите причину отходов…",
    dialogSave: "Сохранить сопоставление",
    dialogPickRequired: "Сначала выберите причину отходов.",
    dialogOffline:
      "Вы не в сети. Изменение справочника требует связи и подождёт, пока она появится.",
    dialogLoadingOptions: "Загружаем причины отходов…",
    dialogOptionsFailed:
      "Причины отходов не загрузились, выбирать не из чего. Закройте окно и попробуйте снова.",
    dialogNoOptions:
      "Активных причин отходов пока нет. Сначала администратор группы заводит их в разделе «Отходы → Настройки».",
    dialogFailed: "Не удалось сохранить сопоставление.",
  },

  editor: {
    itemUnnamed: "Проверка без названия",
    itemFold: "Свернуть",
    itemOpen: "Изменить",
    otherLanguages: "Другие языки",
    newTitle: "Новая точка контроля",
    editTitle: "Изменение точки контроля",
    versionBanner:
      "Сохранение создаст версию {{next}}. Уже открытые проверки продолжат идти по той версии, на которой начались.",
    duplicateBanner:
      "Скопировано с другой точки контроля. Задайте собственный код перед сохранением.",
    loadError: "Не удалось загрузить точку контроля",
    code: "Код",
    name: "Наименование",
    stage: "Этап",
    stageLocked: "Этап фиксируется после создания — он определяет, к чему точку можно привязать.",
    sampling: "Выборка",
    sampleMode: "Сколько проверяем",
    sampleSize: "Сколько единиц",
    samplePct: "Процент",
    aqlLevel: "Уровень контроля",
    aqlValue: "Приемлемый уровень качества",
    behaviour: "Что происходит при браке",
    blocking: "Блокирующая",
    blockingHint: {
      receiving:
        "Принятая продукция удерживается до прохождения проверки — её нельзя выдать или отгрузить.",
      in_process:
        "Ставит блокировку по качеству. Она запрещает доработку на открытом заново шаге, но не следующую операцию. Материал держит карантин.",
      final: "Единицу нельзя распределить в отгрузку, пока проверка не пройдена.",
    },
    autoHold: "При браке отправлять продукцию в карантин",
    autoHoldHint:
      "При браке единица или партия автоматически уходит в карантин и появляется на доске карантина, где ОТК решает, что с ней делать.",
    guidedCut: "Предлагать резку по подсказке при браке",
    trimAllowance: "Припуск с каждой стороны дефекта (м)",
    minSellable: "Минимальный отрезок, который стоит хранить (м)",
    bindings: "Применяется к",
    bindingsOnRouting:
      "Операционные точки привязываются к операции маршрута в редакторе маршрутов справочника, не здесь.",
    bindingMaterial: "Материал",
    bindingProduct: "Изделие",
    bindingHint: "Выберите из справочника",
    bindingUnresolved: "Уже привязано — нет в этом списке",
    addBinding: "Добавить",
    bindingsShowAll: "Показать все ({{count}})",
    removeBinding: "Убрать",
    checkItems: "Проверки",
    itemSeq: "Проверка {{seq}}",
    itemName: "Что проверяем",
    itemType: "Тип",
    itemUom: "Единица",
    itemMin: "Минимум",
    itemTarget: "Номинал",
    itemMax: "Максимум",
    itemChecklist: "Пункты",
    itemChecklistHint: "insulation_color, marking",
    itemRequired: "Обязательна к заполнению",
    itemPhoto: "Нужно фото",
    itemMinPhotos: "Сколько фото",
    itemCoa: "Печатать в сертификате",
    addItem: "Добавить проверку",
    removeItem: "Убрать",
    moveUp: "Выше",
    moveDown: "Ниже",
    instructions: "Инструкция оператору",
    instructionsHint:
      "Показывается на участке над проверками. Оставьте язык пустым, если перевода нет.",
    saveNew: "Создать точку контроля",
    saveVersion: "Сохранить как новую версию",
    saveFailed: "Сохранение отклонено.",
    invalid: "Нужно исправить: {{count}}.",
    errCode: "Код — заглавные буквы, цифры, подчёркивания и дефисы, например CP-CONT-FINAL.",
    errName: "Укажите наименование.",
    errSampleSize: "Сколько единиц? Целое число.",
    errSamplePct: "Процент больше 0 и не больше 100.",
    errInProcessBindings: "Операционные точки привязываются к операции маршрута, не здесь.",
    errFinalBindings: "Приёмочной проверке нужно хотя бы одно изделие.",
    errReceivingBindings: "Входной проверке нужен хотя бы один материал.",
    errBindingProduct: "Вставьте ID изделия.",
    errBindingMaterial: "Вставьте ID материала.",
    errNoItems: "В точке контроля должна быть хотя бы одна проверка.",
    errItemName: "Укажите, что проверяем.",
    errItemDuplicate:
      "Две проверки не могут называться одинаково — на участке идут по списку сверху вниз.",
    errItemUom: "Замеру нужна единица измерения.",
    errItemBounds: "Замеру нужен минимум, максимум или оба.",
    errItemMinMax: "Минимум не может быть больше максимума.",
    errItemDecimal: "До шести знаков после запятой, только цифры.",
    errItemChecklist: "Укажите хотя бы один пункт.",
    errItemPhotos: "Хотя бы одно фото.",
  },

  detail: {
    samplingLabel: "План выборки:",
  },

  queue: {
    title: "Проверки",
    tabsAria: "Очередь проверок",
    tab: {
      open: "Не начаты",
      in_progress: "В работе",
      failed: "Брак — без решения",
      done: "Завершены",
    },
    doneNote:
      "Проверки с результатом «годен». Отменённые здесь не показываются — открывайте их из самой проверки.",
    filterStage: "Этап",
    // 07-D — метка выборки: строки одной выборки приёмки читаются как одно
    // решение.
    // «Партия» — это лот, а не выборка (D-193).
    cohortBadge: "Выборка: {{count}}",
    cohortTitle: "Проверки, созданные одной выборкой приёмки",
    tableCaption: "Проверки, новые сверху",
    colNo: "№",
    colPoint: "Проверка",
    colSubject: "Объект",
    colStage: "Этап",
    colStatus: "Статус",
    colBlocking: "Блокирует",
    colAtRisk: "Под риском",
    atRiskBounded:
      "Стоимость под риском охватывает новейшие записи карантина ({{count}}) — более старые здесь не отмечены. Полная картина денег — на доске карантина.",
    colAge: "Ожидает",
    colCompleted: "Завершена",
    ageHours: "{{hours}} ч",
    notCompleted: "—",
    notBlocking: "—",
    noSubject: "—",
    unresolved: "Решения нет",
    blocks: {
      receiving: "Блокирует приёмку",
      in_process: "Блокировка операции по качеству",
      final: "Блокирует отгрузку",
    },
    loadMore: "Показать ещё",
    error: "Не удалось загрузить очередь проверок",
    empty: {
      openTitle: "Проверять нечего",
      openBody:
        "Проверки появляются здесь при приёмке, после завершения операции или когда их создают вручную.",
      in_progressTitle: "Начатых проверок нет",
      in_progressBody: "Проверка попадает сюда, как только записан первый результат.",
      failedTitle: "Брака без решения нет",
      failedBody: "Забракованная проверка остаётся здесь, пока не решат, что делать с материалом.",
      doneTitle: "Завершённых пока нет",
      doneBody: "Здесь собираются проверки с результатом «годен».",
    },
  },

  run: {
    title: "Проверка",
    loadError: "Не удалось загрузить проверку",
    blocking: "Блокирующая",
    versionPin:
      "Проверка по {{code}} версии {{version}}, которая действовала при начале этой проверки.",
    subjectUnit: "Единица",
    subjectLot: "Партия",
    subjectOperation: "Операция",
    subjectWorkOrder: "Заказ",
    subjectQty: "Количество",
    readOnly: "Проверку вы читаете. Записывать результаты — работа ОТК или участка.",
    closed: {
      passed: "Проверка пройдена. Записывать больше нечего.",
      failed: "Проверка забракована.",
      cancelled: "Проверка отменена.",
    },
    resolvedAs: {
      rework: "Решено — отправлено на доработку.",
      scrap: "Решено — списано и учтено в журнале отходов.",
      hold_disposition: "Решено — решение принято на доске карантина.",
      guided_cut: "Решено — резка по подсказке.",
      accept_deviation: "Решено — отклонение принято, материал остаётся товарным.",
    },
    optional: "Не обязательна",
    bounds: "Допуск {{min}}–{{max}} {{uom}}",
    boundsAtMost: "Не более {{max}} {{uom}}",
    boundsAtLeast: "Не менее {{min}} {{uom}}",
    boundsTarget: "номинал {{target}} {{uom}}",
    unbounded: "любое",
    measuredLabel: "Замер ({{uom}})",
    measureInvalid: "Число, до шести знаков после запятой.",
    computed: {
      pass: "В допуске",
      fail: "Вне допуска",
    },
    noOverride:
      "Здесь решает прибор. Замер вне допуска нельзя записать как годный — запишите брак и решите, что делать с материалом.",
    overrideNote: "Почему бракуете замер, который в допуске?",
    faultPosition: "Место дефекта от конца A (м)",
    record: "Записать",
    addPhoto: "Добавить фото",
    photosPicked: "Прикреплено фото: {{n}}",
    photosNeeded: "Фото: {{have}} из {{need}}",
    photoRefused: "Это фото не подходит — слишком большое или не изображение.",
    progress: "Записано проверок: {{done}} из {{total}}",
    notes: "Примечание — не обязательно",
    complete: "Завершить — годен",
    completeFail: "Завершить — брак",
    saving: "Сохранение…",
    blockedMissing: "Без ответа осталось проверок: {{count}} — {{names}}",
    blockedEvidence: "Фото не хватает по проверкам: {{count}} — {{names}}",
    blockedCause: "Выберите причину брака.",
    causeLead: "Из-за чего брак?",
    causeSearch: "Поиск причины",
    causeSearchHint: "Введите часть названия…",
    causeEmpty: "Причины ещё не заведены. Их добавляет ОТК в настройках точек контроля.",
    sheet: {
      lead: "Решите, что делать с материалом.",
      noAuthority:
        "Брак зафиксирован. Что делать с материалом — решение ОТК: доработка, списание, карантин или принятие отклонения.",
      // A7 — «онлайн», not latin "online": a settled ru register does not
      // switch scripts mid-sentence (D-123 ruling 9's own standard for ru).
      offline: "Нет сети — решение о материале принимается только онлайн. Оно не встаёт в очередь.",
      // D-299 G5/R8: статус — словарь §3.1 модуля 04 (`production.status.wo.*`).
      // Кнопка «Доработка» остаётся активной: можно указать операцию
      // действующего заказа; продолжения названы у заблокированного
      // подтверждения, а «списать/принять» — как свои только для держателя
      // `quality.resolution.dispose` (решение R3).
      reworkOrderNotLive:
        "Заказ этой операции в статусе «{{status}}», открыть её заново нельзя. Для доработки укажите операцию действующего заказа.",
      reworkTargetNotLive:
        "Заказ в статусе «{{status}}». Укажите выше операцию действующего заказа, спишите материал или примите отклонение.",
      reworkTargetNotLiveNoDispose:
        "Заказ в статусе «{{status}}». Укажите операцию действующего заказа или попросите ОТК списать материал либо принять отклонение.",
      reworkTargetChecking: "Проверяем заказ этой операции…",
      reworkTargetUnknown:
        "Операции заказа с таким ID нет. Проверьте его или оставьте поле пустым.",
      reworkTargetNotCompleted:
        "Операция {{seq}} в статусе «{{status}}» — открывать заново нечего. Укажите операцию, законченная работа которой не прошла проверку.",
      reworkTargetLive: "Доработка откроет заново операцию {{seq}}.",
      reworkHeldNoRelease:
        "Операция {{seq}} заблокирована по качеству, а доработать заблокированную операцию может только администратор группы, администратор площадки или контроль качества.",
      reworkHeldNoReleaseRoles:
        "Операция {{seq}} заблокирована по качеству, а доработать заблокированную операцию может только {{roles}}.",
      reworkHeldAsk:
        "Попросите одного из них выполнить эту доработку или сначала снять блокировку.",
      reworkNotPermittedRoles: "Эту доработку может выполнить только {{roles}}. Обратитесь к ним.",
      reworkNotPermitted: "Эта доработка вам недоступна. Обратитесь к руководителю по качеству.",
      act: {
        rework: "Доработка",
        scrap: "Списание",
        quarantine: "Карантин",
        accept_deviation: "Принять отклонение",
        guided_cut: "Разрез по дефекту",
      },
      cut: {
        goHint: "План разреза — на следующем экране",
        why: {
          not_offered_by_control_point:
            "Эта проверка разрез не предусматривает. Доработка, списание или принятие отклонения.",
          subject_not_length_measured:
            "Для разреза нужна измеренная длина кабеля. Эта проверка не про один барабан кабеля.",
          inspection_not_actionable: "Эта проверка не ждёт решения по браку — резать нечего.",
          length_not_measured:
            "Длина этого барабана — заявка станка, а не измерение. Измерьте барабан, и разрез можно будет спланировать.",
          depth_cap:
            "Этот барабан — {{depth}}-й разрез от исходного. Предел — пять. Спишите, понизьте сорт или отправьте на доработку.",
          source_too_short_to_cut:
            "На барабане {{length}} м при обрезке {{trim}} м не останется товарного куска в {{minimum}} м. Спишите, понизьте сорт или отправьте на доработку.",
          source_too_short_to_cut_noMinimum:
            "Барабан {{length}} м не длиннее обрезки {{trim}} м — от разреза ничего не останется. Спишите, понизьте сорт или отправьте на доработку.",
          no_fault_position:
            "Сначала запишите, где на кабеле дефект. План разреза строится вокруг этой отметки.",
          fault_not_on_source:
            "Отметка дефекта не попадает на барабан {{length}} м после перемера. Измерьте снова или исправьте отметку.",
          no_sellable_segment:
            "Обрезка {{trim}} м не оставляет на барабане {{length}} м куска длиннее {{minimum}} м — это списание.",
          no_sellable_segment_noMinimum:
            "При обрезке {{trim}} м вокруг дефекта на барабане {{length}} м не остаётся товарного куска. Разрез, после которого ничего не остаётся, — это списание.",
          unknown:
            "Устройство не может определить, доступен ли здесь разрез. Подключитесь к сети и откройте проверку заново.",
        },
        fix: {
          open: "Измерить барабан",
          label: "Измеренная длина (м)",
          submit: "Сохранить измерение",
          done: "Измерено: {{remaining}} м. Разрез можно будет спланировать после обновления проверки.",
          noGrant: "Измерение остатков — работа кладовщика. Попросите перемерить барабан.",
          offline: "Нет сети — измерение сохраняется только онлайн.",
        },
      },
      confirm: {
        rework: "Отправить на доработку",
        scrap: "Списать материал",
        quarantine: "Отправить в карантин",
        accept_deviation: "Принять отклонение",
      },
      noteLabel: "Примечание — не обязательно",
      noteMandatoryLabel: "Почему отклонение допустимо?",
      reasonLabel: "Почему продукция отправляется в карантин?",
      noteRequired: "Для принятия отклонения обязательно примечание.",
      reasonRequired: "Для карантина обязательна причина.",
      targetLabel: "Операция для повторного открытия — не обязательно",
      targetHint: "Оставьте пустым, чтобы открыть операцию самой проверки",
      targetInvalid: "Вставьте полный ID операции или оставьте поле пустым.",
      done: {
        rework: "Отправлено на доработку — операция открыта повторно.",
        scrap: "Списано — учтено в журнале отходов.",
        quarantine: "В карантине как {{holdNo}}. Запись на доске карантина.",
        accept_deviation: "Отклонение принято — материал остаётся товарным.",
      },
      reworkRef: "Повторно открытая операция:",
      scrapValuation: "Оценка списания: {{amount}}",
      scrapValuationElsewhere: "Оценка списания, если она была записана, — в журнале отходов.",
    },
    sync: {
      applied: "Записано",
      queued: "Ждёт отправки",
      needsReview: "Нужна проверка",
      refused: "Отклонено",
      superseded: "Заменено",
    },
    settled: {
      queued: "Сохранено на устройстве — отправится само, когда вернётся сеть.",
      conflicted: "Это расходится с уже записанным, поэтому не сохранено. Нужен разбор мастером.",
      needs_attention: "Запись отклонена. Она в лотке на этом устройстве.",
      superseded: "Заменено более поздней записью.",
      applied: "Записано.",
    },
    completed: {
      applied: "Проверка завершена.",
      queued: "Завершение сохранено на устройстве — отправится само, когда вернётся сеть.",
      conflicted:
        "Эту проверку не удалось завершить — возможно, кто-то завершил её раньше. Обновите и посмотрите.",
      needs_attention: "Завершение отклонено. Запись в лотке на этом устройстве.",
      superseded: "Заменено более поздней записью.",
    },
    signedOut: "Сессия завершена — войдите снова, чтобы продолжить.",
    cancelOpen: "Отменить проверку…",
    cancelReason: "Почему отменяем?",
    cancelConfirm: "Отменить проверку",
    cancelFailed: "Не удалось отменить — возможно, она уже завершена.",
  },

  floor: {
    scoreboardTitle: "Сводка по сменам",
    scoreboardDate: "Дата",
    scoreboardThing: "сводку по сменам",
    scoreboardError: "Не удалось загрузить сводку.",
    scoreboardErrorBody: "Проверьте связь и повторите попытку.",
    scoreboardEmptyTitle: "На эту дату смен нет",
    scoreboardEmptyBody: "Выберите дату, когда завод работал по сменам.",
    queueTitle: "Проверки",
    runTitle: "Проверка",
    backHome: "Назад",
    backQueue: "К проверкам",
    queueThing: "список проверок",
    runThing: "эту проверку",
    queueError: "Не удалось загрузить проверки",
    queueErrorBody: "Список не пришёл. Повторите, когда появится связь.",
    runError: "Не удалось загрузить проверку",
    runErrorBody: "Проверка не пришла. Повторите, когда появится связь.",
    emptyTitle: "Проверять нечего",
    emptyBody: "Проверки появляются здесь, когда завершается работа или приходит приёмка.",
    refusedTitle: "Не ваш экран",
    refusedBody: "Вашей роли проверки не видны. Обратитесь к мастеру.",
    queuedCount: "Ждут отправки записей: {{count}}",
    needsReview: "Не удалось сохранить записей: {{count}} — нужен разбор мастером.",
  },

  cut: {
    title: "Разрез по дефекту",
    back: "К проверке",
    refusedTitle: "Не ваш экран",
    refusedBody: "Разрез продукции — работа ОТК. Обратитесь к мастеру.",
    onlineOnly:
      "Разрез выполняется только онлайн — он никогда не встаёт в очередь. Найдите связь, чтобы спланировать или выполнить его.",
    loadErrorTitle: "Не удалось загрузить проверку",
    loadErrorBody: "Проверка не пришла. Повторите, когда появится связь.",
    sourceLength: "Измерено {{length}} м",
    depth: "Разрез {{depth}} от исходного барабана",
    notAvailableTitle: "Разрез здесь недоступен",
    notPlannedBody: "Спланируйте разрез — отметки рассчитаются от положения дефекта.",
    plan: "Спланировать разрез",
    planHead: "План",
    snapshot: "Рассчитано по длине {{length}} м, измеренной на момент составления плана.",
    segment: {
      stock: "Оставить — с {{from}} м до {{to}} м",
      waste: "Вырезать — с {{from}} м до {{to}} м",
    },
    segmentLength: "{{length}} м",
    belowMinimum: "Кусок короче минимума в {{minimum}} м уходит в отходы, а не на склад.",
    tilingBroken:
      "Эти куски больше не складываются в целый барабан {{length}} м. Спланируйте разрез заново.",
    tilingBlocked: "Куски не складываются в целый барабан — спланируйте заново.",
    handlesHead: "Где проходит разрез",
    handlesRule: "Отметки двигаются только наружу — внутрь, к дефекту и его обрезке, нельзя.",
    handlesFixed:
      "Эта проверка обрезку не предусматривает: разрез один, точно по дефекту. Двигать нечего.",
    handleA: "Разрез, м от начала",
    handleB: "Разрез, м от начала — дальняя отметка",
    handleProblem: {
      narrowed: "Так разрез пройдёт внутри обрезки, и дефект уйдёт в товарный кусок.",
      topology_changed: "Так пропадёт целый кусок. Спланируйте заново с большей обрезкой.",
      unreadable: "Эти отметки не задают две точки на барабане.",
    },
    summaryHead: "Перед разрезом",
    summaryBody: "Новых барабанов: {{count}}. Каждый останется под запретом до своей перепроверки.",
    confirm: "Отметки совпадают с барабаном",
    execute: "Разрезать барабан",
    executeWarn: "Барабан будет израсходован. Отменить это уже нельзя.",
    executeConfirm: "Да — резать",
    executeStandDown: "Пока нет",
    replan: "Спланировать заново",
    cancel: "Отказаться от плана",
    cancelReason: "Почему отказываемся от плана?",
    cancelReasonRequired: "Укажите причину, чтобы следующий понял.",
    cancelConfirm: "Отказаться от плана",
    cancelledTitle: "От этого плана отказались",
    cancelledBody: "Спланируйте заново, когда будете готовы.",
    lengthChangedHint:
      "Барабан перемерили после составления плана. Спланируйте заново под новую длину.",
    doneTitle: "Разрез выполнен",
    doneUnits: "Новых барабанов: {{count}} — под запретом до перепроверок.",
    doneRetests: "Перепроверок в списке: {{count}}.",
    doneToQueue: "К проверкам",
  },
};
